import { type MediumName, SocialPlatform } from "shared/helpers/media";
import {
  OutreachInsightsMetric,
  OutreachPerformanceMetric,
  SocialEngagePerformanceMetric,
  SocialInsightsMetric,
  StreamInsightsMetric,
} from "shared/modules/Charts/types/chartSettings";
import type { Chart } from "shared/modules/Charts/types/chartTypes";

export const DEFAULT_MAX_SELECTED_INSTAGRAM_ACCOUNTS = 12;

export const DEFAULT_INITIAL_INSTAGRAM_ACCOUNT_SELECTION = 3;

export const CHARTS_WITH_STREAMS_AND_KEY_MESSAGES: Chart[] = [
  "key_messages",
] as const;

export const CHARTS_WITH_STREAMS_AND_SOCIAL_STREAMS: Chart[] = [
  "sentiment_benchmark",
  "share_of_voice_timeline",
  "share_of_voice",
  "coverage_volume_timeline",
  "coverage_volume_and_analytics_timeline",
  "sentiment_breakdown",
  "sentiment_timeline",
  "stream_vs_stream",
] as const;

export const CHARTS_WITH_SOCIAL_STREAMS_ONLY: Chart[] = [
  "coverage_volume_breakdown",
  "leading_hashtags",
  "emoji_count",
  "top_tweets",
  "top_bluesky_posts",
  "top_instagram_posts",
  "top_linkedin_posts",
  "top_tiktok_videos",
  "top_youtube_videos",
  "top_facebook_posts",
  "top_facebook_pages",
  "key_twitter_influencers",
  "key_instagram_influencers",
  "key_youtube_influencers",
  "key_bluesky_influencers",
  "social_insights_summary",
  "key_influencers",
  "average_post_time_by_platform",
  "top_posts",
  "instagram_profile_activity",
] as const;

export const CHARTS_WITH_LINKED_SOCIAL_STREAMS: Chart[] = [
  "stream_vs_stream",
] as const;

export const CHARTS_WITH_OPTIONAL_LINKED_SOCIAL_STREAMS: Chart[] = [
  "coverage_volume_and_stock_price",
  "outreach_coverage_timeline",
  "themes",
  "key_messages",
] as const;

export const CHARTS_WITH_STREAMS_COMPARISON: Chart[] = [
  "share_of_voice",
  "share_of_voice_timeline",
  "mentions_benchmark",
  "competitor_benchmark",
  "sentiment_benchmark",
] as const;

export const CHARTS_WITH_SOV_METRIC_TYPE = [
  "share_of_voice",
  "share_of_voice_timeline",
] as const satisfies Chart[];

export const CHARTS_WITH_STREAMS_AND_MEDIA_RELEASES: Chart[] = [
  "outreach_coverage_timeline",
] as const;

export const CHARTS_WITH_MEDIA_RELEASES_ONLY: Chart[] = [
  "outreach_performance_table",
  "outreach_top_engaged_contacts_table",
  "outreach_insights_summary",
] as const;

export const CHARTS_WITH_OUTREACH_ACTIVITY_SELECTION = [
  "outreach_activity_timeline",
] as const satisfies readonly Chart[];

export const CHARTS_WITH_OUTREACH_CONTACT_LABELS = [
  "leading_online_journalists",
  "leading_online_sources",
  "leading_print_journalists",
  "leading_print_sources",
] as const satisfies readonly Chart[];

export const CHARTS_WITH_SOCIAL_ENGAGE_ONLY: Chart[] = [
  "follower_growth",
  "follower_growth_benchmark",
  "follower_growth_benchmark_by_account",
  "instagram_public_account_follower_growth",
  "instagram_public_and_owned_account_followers_benchmark",
  "instagram_profile_activity",
  "share_of_voice_follower_count",
  "social_engage_performance",
  "social_post_highlight",
  "top_performing_post",
  "engagement_timeline_for_social_post",
  "share_of_voice_social_post_engagement",
  "net_follower_growth",
] as const;

export const CHARTS_WITH_EXPORT_OPTIONS: Chart[] = [
  "outreach_top_engaged_contacts_table",
  "top_articles_table",
] as const;

export const CHARTS_WITH_STREAM_INSIGHTS_METRICS = [
  "mention_stream_insights_summary",
] as const satisfies Chart[];

export const CHARTS_WITH_OUTREACH_INSIGHTS_METRICS = [
  "outreach_insights_summary",
] as const satisfies Chart[];

export const CHARTS_WITH_OUTREACH_PERFORMANCE_METRICS = [
  "outreach_performance_table",
] as const satisfies Chart[];

export const CHARTS_WITH_SOCIAL_INSIGHTS_METRICS = [
  "social_insights_summary",
] as const satisfies Chart[];

export const CHARTS_WITH_SOCIAL_ENGAGE_PERFORMANCE_METRICS = [
  "social_engage_performance",
  "share_of_voice_social_post_engagement",
] as const satisfies Chart[];

export const CHARTS_WITH_SOCIAL_ENGAGE_INSIGHTS_METRICS = [
  "social_engage_performance",
] as const satisfies Chart[];

export const CHARTS_WITH_POST_SELECTION: Chart[] = [
  "follower_growth",
  "follower_growth_benchmark",
  "follower_growth_benchmark_by_account",
  "share_of_voice_follower_count",
  "share_of_voice_social_post_engagement",
  "instagram_profile_activity",
  "social_engage_performance",
  "social_post_highlight",
  "engagement_timeline_for_social_post",
] as const;

export const CHARTS_WITH_EARLIEST_POSTED_AT = [
  "engagement_timeline_for_social_post",
  "share_of_voice_social_post_engagement",
] as const satisfies readonly Chart[];

export const CHARTS_WITHOUT_SOCIAL_ITEMS_TAB: Chart[] = [
  "follower_growth",
  "net_follower_growth",
  "follower_growth_benchmark",
  "follower_growth_benchmark_by_account",
  "key_influencers",
  "share_of_voice_follower_count",
  "instagram_public_account_follower_growth",
  "instagram_public_and_owned_account_followers_benchmark",
  "instagram_profile_activity",
  "average_post_time_by_platform",
  "top_posts",
] as const;

export const CHARTS_WITHOUT_SOCIAL_POSTS_TAB: Chart[] = [
  "follower_growth",
  "follower_growth_benchmark",
  "follower_growth_benchmark_by_account",
  "share_of_voice_follower_count",
] as const;

export const CHARTS_WITH_SOCIAL_ACCOUNT_SELECTION: Chart[] = [
  "follower_growth",
  "follower_growth_benchmark",
  "follower_growth_benchmark_by_account",
  "share_of_voice_follower_count",
  "social_engage_performance",
  "top_performing_post",
  "net_follower_growth",
  "engagement_timeline_for_social_post",
  "instagram_public_and_owned_account_followers_benchmark",
  "instagram_profile_activity",
  "social_post_highlight",
] as const;

export const CHARTS_WITH_INSTAGRAM_HANDLE_SELECTION = [
  "instagram_public_account_follower_growth",
  "instagram_public_and_owned_account_followers_benchmark",
] as const satisfies readonly Chart[];

export const CHARTS_WITH_SOCIAL_ACCOUNT_SELECTION_IN_POSTS_TAB: Chart[] = [
  "engagement_timeline_for_social_post",
  "instagram_profile_activity",
  "share_of_voice_social_post_engagement",
  "social_post_highlight",
] as const;

export const CHARTS_WITHOUT_SOCIAL_ACCOUNTS_ON_CHART_TAB: Chart[] = [
  "instagram_public_account_follower_growth",
  "instagram_public_and_owned_account_followers_benchmark",
] as const;

export const CHARTS_WITH_INSIGHTS_METRICS = [
  ...CHARTS_WITH_STREAM_INSIGHTS_METRICS,
  ...CHARTS_WITH_OUTREACH_INSIGHTS_METRICS,
  ...CHARTS_WITH_OUTREACH_PERFORMANCE_METRICS,
  ...CHARTS_WITH_SOCIAL_INSIGHTS_METRICS,
  ...CHARTS_WITH_SOCIAL_ENGAGE_INSIGHTS_METRICS,
] as const satisfies Chart[];

export const CHARTS_WITH_SORT_BY: Chart[] = ["top_articles_table"] as const;

export const DEFAULT_CHARTS: Chart[] = [
  "coverage_volume_timeline",
  "coverage_volume_and_analytics_timeline",
  "coverage_volume_and_potential_audience_reach_timeline",
  "coverage_volume_and_potential_audience_reach_table",
  "coverage_by_media_type",
  "media_impact_timeline",
  "coverage_volume_and_referral_traffic_timeline",
  "leading_online_journalists",
  "leading_online_sources",
  "leading_print_journalists",
  "leading_print_sources",
  "leading_radio_programs",
  "leading_tv_programs",
  "leading_brands",
  "themes",
  "top_articles_table",
  "sentiment_breakdown",
  "sentiment_timeline",
  "share_of_voice",
  "share_of_voice_timeline",
  "geographic_spread_city_chart",
  "geographic_spread_city_map",
  "geographic_spread_state_chart",
  "geographic_spread_state_map",
  "geographic_spread_region_chart",
  "geographic_spread_region_map",
  "total_audience_reach",
  "advertising_value_equivalency",
  "potential_audience_reach",
  "domain_authority",
  "react_score_breakdown",
] as const;

export const CHARTS_WITH_AI_VISIBILITY: Chart[] = [
  "coverage_volume_and_stock_price",
  "coverage_volume_timeline",
  "outreach_coverage_timeline",
  "share_of_voice_timeline",
  "share_of_voice",
  "trajaan_sentiment",
  "trajaan_top_domains",
  "trajaan_top_authors",
  "trajaan_top_outlets",
  "trajaan_visibility",
  "trajaan_word_cloud",
];

export const CHARTS_WITH_OPTIONAL_MENTION_STREAMS = [
  "coverage_volume_and_stock_price",
  "coverage_volume_timeline",
  "outreach_coverage_timeline",
  "share_of_voice",
  "share_of_voice_timeline",
] as const satisfies readonly Chart[];

export const CHARTS_WITH_ONLY_AI_VISIBILITY: Chart[] = [
  "trajaan_sentiment",
  "trajaan_top_domains",
  "trajaan_top_authors",
  "trajaan_top_outlets",
  "trajaan_visibility",
  "trajaan_word_cloud",
];

export const CHARTS_WITH_CONTACT_KEYS: Chart[] = [
  "leading_online_journalists",
  "leading_online_sources",
  "leading_print_journalists",
  "leading_print_sources",
  "trajaan_top_authors",
];

export const CHARTS_WITH_MEDIA_RELEASES_HEADER: Chart[] = [
  "outreach_top_engaged_contacts_table",
] as const;

export const CHARTS_WITH_SINGLE_MEDIA_RELEASE: Chart[] = [
  "outreach_top_engaged_contacts_table",
] as const;

export const CHARTS_WITH_GEOGRAPHIC_SPREAD = [
  "geographic_spread_city_chart",
  "geographic_spread_city_map",
  "geographic_spread_state_chart",
  "geographic_spread_state_map",
  "geographic_spread_region_chart",
  "geographic_spread_region_map",
] as const satisfies readonly Chart[];

export const OUTREACH_CHARTS: Chart[] = [
  "outreach_activity_timeline",
  "outreach_coverage_timeline",
  "outreach_insights_summary",
  "outreach_performance_table",
  "outreach_top_engaged_contacts_table",
] as const;

export const DOUBLE_WIDTH_WHEN_MANY_STREAMS_CHARTS: Chart[] = [
  "share_of_voice",
  "share_of_voice_timeline",
] as const;

export const CHARTS_WITH_INTERVAL: Chart[] = [
  "coverage_volume_timeline",
  "coverage_volume_and_potential_audience_reach_timeline",
  "coverage_volume_and_analytics_timeline",
  "coverage_by_media_type",
  "share_of_voice_timeline",
  "potential_audience_reach",
  "advertising_value_equivalency",
  "sentiment_timeline",
  "react_score_timeline",
  "stream_vs_stream",
  "coverage_volume_and_referral_traffic_timeline",
  "outreach_activity_timeline",
  "outreach_coverage_timeline",
  "mention_benchmark_timeline",
  "coverage_volume_and_stock_price",
  "total_social_shares",
  "follower_growth",
  "instagram_public_account_follower_growth",
  "net_follower_growth",
] as const;

export const CHARTS_WITH_STACKED_HISTOGRAM: Chart[] = [
  "advertising_value_equivalency",
  "coverage_by_media_type",
  "potential_audience_reach",
] as const;

export const CHARTS_WITH_CUSTOM_EVENTS: Chart[] = [
  "outreach_performance_table",
  "outreach_top_engaged_contacts_table",
  "top_articles_table",
  "outreach_coverage_timeline",
  "top_facebook_posts",
  "top_tweets",
  "top_bluesky_posts",
  "top_instagram_posts",
  "top_linkedin_posts",
  "top_tiktok_videos",
  "top_youtube_videos",
] as const;

export const CHARTS_WITH_ADD_TO_LIST: Chart[] = [
  "leading_online_journalists",
  "leading_online_sources",
  "leading_print_journalists",
  "leading_print_sources",
  "trajaan_top_authors",
  "trajaan_top_outlets",
] as const;

export const DEFAULT_MIN_SELECTED_STREAMS = 1;

export const DEFAULT_MAX_SELECTED_STREAMS = 12;

export const CHARTS_WITH_FIXED_MEDIUM = [
  "leading_print_sources",
  "leading_print_journalists",
  "leading_online_sources",
  "leading_online_journalists",
  "leading_tv_programs",
  "leading_radio_programs",
  "facebook_engagement_metric",
  "total_social_shares",
] as const satisfies readonly Chart[];

export type ChartWithFixedMedium = (typeof CHARTS_WITH_FIXED_MEDIUM)[number];

export const CHARTS_WITH_FIXED_PLATFORM = [
  "top_tweets",
  "top_facebook_posts",
  "top_facebook_pages",
  "top_bluesky_posts",
  "top_instagram_posts",
  "top_linkedin_posts",
  "top_tiktok_videos",
  "top_youtube_videos",
  "key_twitter_influencers",
  "key_instagram_influencers",
  "key_bluesky_influencers",
  "key_youtube_influencers",
] as const satisfies readonly Chart[];

export type ChartWithFixedPlatform =
  (typeof CHARTS_WITH_FIXED_PLATFORM)[number];

export const ChartsWithFixedMediumMap: Record<
  ChartWithFixedMedium,
  MediumName
> = {
  facebook_engagement_metric: "Online",
  leading_online_journalists: "Online",
  leading_online_sources: "Online",
  leading_print_journalists: "Print",
  leading_print_sources: "Print",
  leading_radio_programs: "Radio",
  leading_tv_programs: "TV",
  total_social_shares: "Online",
};

export const ChartsWithFixedPlatformMap: Record<
  ChartWithFixedPlatform,
  SocialPlatform
> = {
  key_bluesky_influencers: SocialPlatform.BLUESKY,
  key_instagram_influencers: SocialPlatform.INSTAGRAM,
  key_twitter_influencers: SocialPlatform.TWITTER,
  key_youtube_influencers: SocialPlatform.YOUTUBE,
  top_bluesky_posts: SocialPlatform.BLUESKY,
  top_facebook_pages: SocialPlatform.FACEBOOK,
  top_facebook_posts: SocialPlatform.FACEBOOK,
  top_instagram_posts: SocialPlatform.INSTAGRAM,
  top_linkedin_posts: SocialPlatform.LINKEDIN,
  top_tiktok_videos: SocialPlatform.TIKTOK,
  top_tweets: SocialPlatform.TWITTER,
  top_youtube_videos: SocialPlatform.YOUTUBE,
};

export const DEFAULT_OUTREACH_INSIGHTS_METRICS: OutreachInsightsMetric[] = [
  OutreachInsightsMetric.Sent,
  OutreachInsightsMetric.ContactsTargeted,
  OutreachInsightsMetric.DeliveredPercentage,
  OutreachInsightsMetric.Opened,
  OutreachInsightsMetric.OpenedPercentage,
  OutreachInsightsMetric.ClickedThrough,
];

export const DEFAULT_OUTREACH_PERFORMANCE_METRICS: OutreachPerformanceMetric[] =
  [
    OutreachPerformanceMetric.MediaOutreach,
    OutreachPerformanceMetric.Opens,
    OutreachPerformanceMetric.Clicks,
  ];

export const DEFAULT_STREAM_INSIGHTS_METRICS =
  Object.values(StreamInsightsMetric);

export const DEFAULT_SOCIAL_INSIGHTS_METRICS =
  Object.values(SocialInsightsMetric);

export const DEFAULT_SOCIAL_ENGAGE_PERFORMANCE_METRICS: SocialEngagePerformanceMetric[] =
  Object.values(SocialEngagePerformanceMetric);
